import {AuthUsersGuard} from "../guards/authUsers.guard";
import {BreadcrumbsResolver} from "../modules/breadcrumbs/breadcrumbs.resolver";
import {FormChangesGuard} from "../guards/formChanges.guard";

type Entity =
  'store' |
  'store-collection' |
  'store-group' |
  'store-version' |
  'store-key' |
  'account' |
  'product' |
  'project' |
  'purchase' |
  'receipt' |
  'user' |
  'custom-data' |
  'action';

interface IConfig {
  component: any;
  children?: any;
  prefix?: boolean;
  outer?: boolean;
  permission?: string
}

export class RouterHandler {

  entity: any = {
    'store': {
      index: {
        data: {
          title: 'Stores',
          breadcrumbs: 'Stores'
        },
      },
      create: {
        data: {
          title: 'Create store',
        },
      },
      view: {
        data: {
          key: 'store',
          breadcrumbs: 'Store {{ breadcrumbs }}',
        },
      },
      prefix: 'stores',
      redirect: {
        view: 'projects'
      }
    },

    'store-collection': {
      index: {
        data: {
          title: 'Store collections',
          breadcrumbs: 'Store collections'
        },
      },
      create: {
        data: {
          title: 'Create store collection',
        },
      },
      view: {
        data: {
          key: 'storeCollection',
          breadcrumbs: 'Store collection {{ breadcrumbs }}',
        },
      },
      prefix: 'store-collections',
      redirect: {
        view: 'info'
      }
    },

    'store-group': {
      index: {
        data: {
          title: 'Store groups',
          breadcrumbs: 'Store groups'
        },
      },
      create: {
        data: {
          title: 'Create store group',
        },
      },
      edit: {
        data: {
          key: 'storeGroup',
        },
      },
      prefix: 'store-groups',
    },

    'store-version': {
      index: {
        data: {
          title: 'Store versions',
          breadcrumbs: 'Store versions'
        },
      },
      create: {
        data: {
          title: 'Create store version',
        },
      },
      edit: {
        data: {
          key: 'storeVersion',
        },
      },
      prefix: 'store-versions',
    },

    'custom-data': {
      index: {
        data: {
          title: 'Custom data',
          breadcrumbs: 'Custom data'
        },
      },
      create: {
        data: {
          title: 'Create custom data',
        },
      },
      edit: {
        data: {
          key: 'customData',
        },
      },
      prefix: 'custom-data',
    },

    'store-key': {
      index: {
        data: {
          title: 'Store keys',
          breadcrumbs: 'Store keys'
        },
      },
      create: {
        data: {
          title: 'Create store key',
        },
      },
      edit: {
        data: {
          key: 'storeKey',
        },
      },
      prefix: 'store-keys',
    },

    'project': {
      index: {
        data: {
          title: 'Projects',
          breadcrumbs: 'Projects'
        },
      },
      create: {
        data: {
          title: 'Create project',
        },
      },
      view: {
        data: {
          key: 'project',
          breadcrumbs: 'Project {{ breadcrumbs }}',
        },
      },
      prefix: 'projects',
      redirect: {
        view: 'info'
      }
    },

    'product': {
      index: {
        data: {
          title: 'Products',
          breadcrumbs: 'Products'
        },
      },
      create: {
        data: {
          title: 'Create product',
        },
      },
      view: {
        data: {
          key: 'product',
          breadcrumbs: 'Product {{ breadcrumbs }}',
        },
      },
      prefix: 'products',
      redirect: {
        view: 'info'
      }
    },

    'purchase': {
      index: {
        data: {
          title: 'Purchases',
          breadcrumbs: 'Purchases'
        },
      },
      create: {
        data: {
          title: 'Create purchase',
        },
      },
      view: {
        data: {
          key: 'purchase',
          breadcrumbs: 'Purchase {{ breadcrumbs }}',
        },
      },
      prefix: 'products',
      redirect: {
        view: 'info'
      }
    },

    'receipt': {
      index: {
        data: {
          title: 'Purchases',
          breadcrumbs: 'Purchases'
        },
      },
    },

    'user': {
      index: {
        data: {
          title: 'Users',
          breadcrumbs: 'Users'
        },
      },
      view: {
        data: {
          key: 'user',
          breadcrumbs: 'User {{ breadcrumbs }}',
        },
      },
      prefix: 'users',
      redirect: {
        view: 'info'
      }
    },

    'account': {
      index: {
        data: {
          title: 'Accounts',
          breadcrumbs: 'Accounts'
        },
      },
      view: {
        data: {
          key: 'account',
          breadcrumbs: 'Account {{ breadcrumbs }}',
        },
      },
      prefix: 'accounts',
      redirect: {
        view: 'users'
      }
    },

    'action': {
      index: {
        data: {
          title: 'Actions',
          breadcrumbs: 'Actions'
        },
      },
      create: {
        data: {
          title: 'Create action',
        },
      },
      edit: {
        data: {
          key: 'action',
        },
      },
      prefix: 'actions'
    },
  };

  redirect(type: Entity) {
    return {
      path: '',
      redirectTo: this.entity[type].redirect.view,
      pathMatch: 'full'
    }
  }

  index(type: Entity, config: IConfig) {
    return {
      path: '',
      canActivate: [AuthUsersGuard],
      deactivate: [FormChangesGuard],
      component: config.component,
      ...this.entity[type].index
    };
  }

  create(type: Entity, config: IConfig) {
    return {
      path: 'create',
      component: config.component,
      canActivate: [AuthUsersGuard],
      canDeactivate: [FormChangesGuard],
      ...this.entity[type].create,
      data: {
        breadcrumbs: 'Create',
        outer: config.outer,
        ...this.entity[type].create.data
      },
    };
  }

  view(type: Entity, config: IConfig) {
    return {
      path: (config.prefix ? this.entity[type].prefix + '/' : '') + 'view/:id',
      resolve: {
        breadcrumbs: BreadcrumbsResolver
      },
      canActivate: [AuthUsersGuard],
      component: config.component,
      children: config.children || [],
      ...this.entity[type].view,
      data: {
        outer: config.outer,
        ...this.entity[type].view.data
      },
    };
  }

  info(type: Entity, config: IConfig) {
    return {
      path: 'info',
      data: { title: 'Info' },
      component: config.component,
      children: config.children || []
    }
  }


  customData(type: Entity, config: IConfig) {
    return {
      path: 'custom-data',
      data: { title: 'Custom Data' },
      component: config.component,
      children: config.children || []
    }
  }


  projects(type: Entity, config: IConfig) {
    return {
      path: 'projects',
      data: {title: 'Projects', breadcrumbs: 'Projects'},
      canDeactivate: [FormChangesGuard],
      component: config.component,
      children: config.children || []
    }
  }

  purchases(type: Entity, config: IConfig) {
    return {
      path: 'purchases',
      data: {title: 'Purchases', breadcrumbs: 'Purchases'},
      component: config.component,
      children: config.children || []
    }
  }

  snapshots(type: Entity, config: IConfig) {
    return {
      path: 'snapshots',
      data: {title: 'Snapshots', breadcrumbs: 'Snapshots', permission: config.permission},
      component: config.component,
      children: config.children || [],
    }
  }

  products(type: Entity, config: IConfig) {
    return {
      path: 'products',
      data: {title: 'Products', breadcrumbs: 'Products'},
      canDeactivate: [FormChangesGuard],
      component: config.component,
      children: config.children || []
    }
  }

  storeCollections(type: Entity, config: IConfig) {
    return {
      path: 'store-collections',
      data: {title: 'Collections', breadcrumbs: 'Collections'},
      canDeactivate: [FormChangesGuard],
      component: config.component,
      children: config.children || []
    }
  }

  versions(type: Entity, config: IConfig) {
    return {
      path: 'store-versions',
      data: {title: 'Versions', breadcrumbs: 'Versions'},
      canDeactivate: [FormChangesGuard],
      component: config.component,
      children: config.children || []
    }
  }

  actions(type: Entity, config: IConfig) {
    return {
      path: 'actions',
      data: {title: 'Actions', breadcrumbs: 'Actions'},
      canDeactivate: [FormChangesGuard],
      component: config.component,
      children: config.children || []
    }
  }

  coinFlow(type: Entity, config: IConfig) {
    return {
      path: 'coin-flow',
      data: {title: 'Coin flow', breadcrumbs: 'Coin flow'},
      component: config.component,
      children: config.children || []
    }
  }

  users(type: Entity, config: IConfig) {
    return {
      path: 'users',
      data: {title: 'Users', breadcrumbs: 'Users'},
      component: config.component,
      children: config.children || []
    }
  }

  edit(type: Entity, config: IConfig) {
    switch (type) {
      case 'action':
      case 'store-version':
      case 'store-group':
      case 'custom-data':
      case 'store-key':
        return {
          path: (config.prefix ? this.entity[type].prefix + '/' : '') + 'edit/:id',
          resolve: {
            breadcrumbs: BreadcrumbsResolver
          },
          canDeactivate: [FormChangesGuard],
          canActivate: [AuthUsersGuard],
          component: config.component,
          ...this.entity[type].edit,
          data: {
            breadcrumbs: 'Edit {{ breadcrumbs }}',
            outer: config.outer,
            ...this.entity[type].edit.data
          },
        };
      default:
        return {
          path: 'edit',
          data: {title: 'Edit', breadcrumbs: 'Edit'},
          canDeactivate: [FormChangesGuard],
          canActivate: [AuthUsersGuard],
          component: config.component,
          children: config.children || [],
        }
    }
  }

  injectionChildren(source: any, childrenByKey: any) {
    const data = {};

    for (let key in childrenByKey) {
      if (childrenByKey.hasOwnProperty(key)) {
        data[key] = {...source[key], children: childrenByKey[key]};
      }
    }

    return Object.assign({}, source, data);
  }

  getRoutes(routes, withChildren: boolean) {
    const children = routes.slice();

    if (withChildren) {
      return children;
    } else {
      return children.map(route => {
        let body = {...route};

        if (body.children) {
          delete body.children;
        }

        return body;
      })
    }
  }
}

export const routerHandler = new RouterHandler();
